import React, { Fragment, useMemo } from 'react';
import useTranslation from 'next-translate/useTranslation';
import { TypeOf } from 'yup';
import { css, cx } from '@linaria/core';
import { useAtom } from 'jotai';
import { focusAtom } from 'jotai/optics';
import Button from '../../../components/Button';
import { Modal, colors } from '@spaceshipapp/cambridge';
import itemSchema from '../../../schemas/ship/item';
import Currency from '../../../utils/currency';
import ItemForm from '../../ItemForm';
import { itemMapAtom } from '../../../contexts/shipment';
import { prohibitedHsCodeMapAtom } from '../../../contexts/quotation';
import { ItemFormModalContainer } from './styled';

interface Props {
  itemId: string;
  onRemove(): void;
}

const ItemBtn: React.FC<Props> = (props) => {
  const { itemId } = props;
  const { t } = useTranslation('common');
  const itemAtom = useMemo(() => focusAtom(itemMapAtom, (o) => o.prop(itemId)), [itemMapAtom]);
  const [item, updateItem] = useAtom(itemAtom);
  const [prohibitedHsCodeMap] = useAtom(prohibitedHsCodeMapAtom);
  const {
    description,
    qty,
    value,
    hkhsCode,
    isIncludesFood,
    isIncludesBattery,
    countryOfManufacture,
    remarks,
  } = item || {};
  const isError = !!(hkhsCode && prohibitedHsCodeMap[hkhsCode]);

  const handleEditItem = (data: TypeOf<typeof itemSchema>) => {
    const {
      currency,
      naturalAmount,
      description,
      qty,
      hkhsCode,
      isIncludesFood,
      isIncludesBattery,
      countryOfManufacture,
      remarks,
    } = data;
    const value = Currency.convert(naturalAmount, currency);
    updateItem({
      description,
      value,
      qty,
      hkhsCode,
      isIncludesFood,
      isIncludesBattery,
      countryOfManufacture,
      remarks,
    });
  };

  const label = t('Items.buttonOpenEditItem', {
    description,
    qty,
    value: Currency.format(value),
  });

  if (!itemId || !item) return null;

  return (
    <ItemFormModalContainer>
      <Modal
        title={t('Items.editItemTitle')}
        trigger={
          <Button size="sm" iconName="Pen" className={cx(itemBtn, isError && errorStyle)}>
            {label}
          </Button>
        }
      >
        <ItemForm
          description={description}
          qty={qty}
          hkhsCode={hkhsCode}
          currency={value.currency}
          naturalAmount={Currency.parse(value)}
          isIncludesFood={isIncludesFood}
          isIncludesBattery={isIncludesBattery}
          countryOfManufacture={countryOfManufacture}
          remarks={remarks}
          onSubmit={handleEditItem}
          onRemove={props.onRemove}
        />
      </Modal>
    </ItemFormModalContainer>
  );
};

const itemBtn = css`
  display: inline-flex;
  flex-direction: row-reverse;

  svg ~ span {
    margin-left: 0;
    margin-right: 0.5em;
  }
`;

const errorStyle = css`
  box-shadow: 0 0 0 2px ${colors.error};
`;

export default ItemBtn;
